import React, { useState, useEffect, useCallback } from "react";
import useOffersData from "../hooks/useOffersData";
import Pagination from "./Pagination";
import EditOfferModal from "./EditOfferModal";
import GenerateOfferPDF from "./GenerateOfferPDF";
import Button from "@mui/material/Button";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTheme } from "../context/ThemeContext";
import DeleteOfferModal from "./DeleteOfferModal";
import SearchIcon from "@mui/icons-material/Search";
import API_BASE_URL from "../config";
import ConfirmOfferStatusModal from "./ConfirmOfferStatusModal";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { getUserId } from "../utils/Auth";

const AllOffersList = () => {
  const { offers, fetchAllOffers, deleteOffer, toggleOfferStatus } =
    useOffersData();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const offersPerPage = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedUser, setSelectedUser] = useState("");
  const [allUsers, setAllUsers] = useState([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [offerToDelete, setOfferToDelete] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isConfirmStatusModalOpen, setIsConfirmStatusModalOpen] =
    useState(false);
  const [offerToToggle, setOfferToToggle] = useState(null);
  const [statusToEdit, setStatusToEdit] = useState(null);
  const [refusalReasonToView, setRefusalReasonToView] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const { darkMode } = useTheme();
  const [userData, setUserData] = useState(null);
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);


  const getStatusLabel = (status) => {
    switch (status) {
      case 0:
        return "Prospect";
      case 1:
        return "Finalizat";
      case 2:
        return "Refuzat";
      default:
        return "Necunoscut";
    }
  };

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const token = localStorage.getItem("token");
        const userId = getUserId();
        if (!userId) return;

        const response = await fetch(`${API_BASE_URL}/api/users/${userId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) throw new Error("Hiba a felhasználó lekérésekor.");
        const data = await response.json();
        setUserData(data);
      } catch (err) {
        console.error("Hiba a felhasználó lekérésekor:", err);
      }
    };

    fetchUserData();
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE_URL}/api/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error("Hiba a felhasználók lekérésekor.");
      }

      const data = await response.json();
      setAllUsers(data);
    } catch (err) {
      console.error("Hiba a felhasználók lekérésekor:", err);
    }
  }, []);

  useEffect(() => {
    fetchAllOffers(selectedYear);
  }, [fetchAllOffers, selectedYear]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedUser, sortConfig]);

  const filteredOffers = offers.filter((offer) => {
    const searchNormalized = searchQuery.trim().toLowerCase();

    return (
      (!selectedUser || offer.userId === parseInt(selectedUser)) &&
      [
        offer.nr.toString(), // 🔍 Keresés ajánlatszám alapján
        offer.userName, // 🔍 Keresés ügynök neve alapján
        offer.customerName, // 🔍 Keresés ügyfél neve alapján
        offer.subtotal.toFixed(2), // 🔍 Keresés részösszeg alapján
        offer.tva.toFixed(2), // 🔍 Keresés ÁFA alapján
        offer.total.toFixed(2), // 🔍 Keresés végösszeg alapján
      ].some((field) => field.toLowerCase().includes(searchNormalized))
    );
  });

  const sortedOffers = [...filteredOffers].sort((a, b) => {
    if (!sortConfig.key) return 0;

    let valA = a[sortConfig.key];
    let valB = b[sortConfig.key];

    if (sortConfig.key === "createdAt") {
      valA = new Date(valA);
      valB = new Date(valB);
    } else if (sortConfig.key === "total" || sortConfig.key === "discount") {
      valA = parseFloat(valA);
      valB = parseFloat(valB);
    } else if (typeof valA === "string" && typeof valB === "string") {
      valA = valA.toLowerCase();
      valB = valB.toLowerCase();
    }

    if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
    if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
    return 0;
  });

  // 🔍 Szűrt ajánlatok lapozása
  const totalPages = Math.ceil(filteredOffers.length / offersPerPage);
  const currentOffers = sortedOffers.slice(
    (currentPage - 1) * offersPerPage,
    currentPage * offersPerPage
  );

  const handleOfferEdited = () => {
    fetchAllOffers(selectedYear);
    setIsEditModalOpen(false);
  };

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      return { key, direction: "asc" };
    });
  };

  return (
    <div
      className={`min-h-screen p-6 ${darkMode ? "bg-gray-900" : "bg-gray-200"}`} // 🔥 Dynamically apply dark mode
    >
      <div className="flex justify-between items-center mb-4">
        <h2
          className={`text-2xl font-semibold ${darkMode ? "text-white" : "text-black"
            }`}
        >
          Toate ofertele
        </h2>

        <div className="relative flex items-center gap-4">
          <SearchIcon
            className={`absolute left-3 ${darkMode ? "text-gray-300" : "text-gray-500"
              }`}
          />
          <input
            type="text"
            placeholder="Căutare"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={`pl-10 pr-4 py-2 border rounded-3xl shadow-sm w-96 transition-all ${darkMode
              ? "bg-gray-800 text-white border-gray-600 placeholder-gray-400"
              : "bg-white text-gray-900 border-gray-300 placeholder-gray-500"
              }`}
          />

          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className={`p-2 border rounded-3xl ${darkMode
                ? "bg-gray-800 text-white border-gray-700"
                : "bg-white text-black border-gray-400"
              }`}
          >
            {[currentYear, currentYear - 1].map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>

          {/* 🔥 Felhasználó szűrő */}
          <div className="flex gap-4">
            <select
              className={`p-2 border rounded-3xl ${darkMode
                ? "bg-gray-800 text-white border-gray-700"
                : "bg-white text-black border-gray-400"
                }`} // 🔥 Style based on dark mode
              value={selectedUser}
              onChange={(e) => setSelectedUser(e.target.value)}
            >
              <option value="">Toți agenții</option>
              {allUsers.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name} ({user.email})
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      <div
        className={`overflow-x-auto mb-4 ${darkMode ? "bg-gray-900" : "bg-white"
          }`}
      >
        <table
          className={`w-full border ${darkMode
            ? "border-gray-700 bg-gray-900 text-gray-300"
            : "border-gray-400 bg-white text-gray-800"
            }`} // 🔥 Table style based on dark mode
        >
          <thead>
            <tr
              className={`${darkMode ? "bg-gray-700 text-white" : "bg-gray-300 text-black"
                }`}
            >
              <th className="border p-2">Nr. Ofertă</th>
              <th className="border p-2">Agent</th>
              <th className="border p-2">Client</th>
              <th className="border p-2">Telefon</th>
              <th className="border p-2">Subtotal</th>
              <th className="border p-2">TVA</th>
              <th
                className="border p-2 cursor-pointer"
                onClick={() => handleSort("total")}
              >
                <div className="flex items-center justify-center gap-1">
                  <span>Total</span>
                  <div className="flex flex-col items-center leading-none gap-[2px]">
                    <div className="flex items-center gap-1">
                      <ArrowUpwardIcon
                        fontSize="small"
                        className={
                          sortConfig.key === "total" &&
                            sortConfig.direction === "asc"
                            ? ""
                            : "opacity-30"
                        }
                      />
                      <span
                        className={`text-xs ${sortConfig.key === "total" &&
                          sortConfig.direction === "asc"
                          ? ""
                          : "opacity-30"
                          }`}
                      >
                        A
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <ArrowDownwardIcon
                        fontSize="small"
                        className={
                          sortConfig.key === "total" &&
                            sortConfig.direction === "desc"
                            ? ""
                            : "opacity-30"
                        }
                      />
                      <span
                        className={`text-xs ${sortConfig.key === "total" &&
                          sortConfig.direction === "desc"
                          ? ""
                          : "opacity-30"
                          }`}
                      >
                        Z
                      </span>
                    </div>
                  </div>
                </div>
              </th>

              <th
                className="border p-2 cursor-pointer"
                onClick={() => handleSort("createdAt")}
              >
                <div className="flex items-center justify-center gap-1">
                  <span>Data</span>
                  <div className="flex flex-col items-center leading-none gap-[2px]">
                    <div className="flex items-center gap-1">
                      <ArrowUpwardIcon
                        fontSize="small"
                        className={
                          sortConfig.key === "createdAt" &&
                            sortConfig.direction === "asc"
                            ? ""
                            : "opacity-30"
                        }
                      />
                      <span
                        className={`text-xs ${sortConfig.key === "createdAt" &&
                          sortConfig.direction === "asc"
                          ? ""
                          : "opacity-30"
                          }`}
                      >
                        A
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <ArrowDownwardIcon
                        fontSize="small"
                        className={
                          sortConfig.key === "createdAt" &&
                            sortConfig.direction === "desc"
                            ? ""
                            : "opacity-30"
                        }
                      />
                      <span
                        className={`text-xs ${sortConfig.key === "createdAt" &&
                          sortConfig.direction === "desc"
                          ? ""
                          : "opacity-30"
                          }`}
                      >
                        Z
                      </span>
                    </div>
                  </div>
                </div>
              </th>

              <th
                className="border p-2 cursor-pointer"
                onClick={() => handleSort("status")}
              >
                <div className="flex items-center justify-center gap-1">
                  <span>Status</span>
                  <div className="flex flex-col items-center leading-none gap-[2px]">
                    <div className="flex items-center gap-1">
                      <ArrowUpwardIcon
                        fontSize="small"
                        className={
                          sortConfig.key === "status" &&
                            sortConfig.direction === "asc"
                            ? ""
                            : "opacity-30"
                        }
                      />
                      <span
                        className={`text-xs ${sortConfig.key === "status" &&
                          sortConfig.direction === "asc"
                          ? ""
                          : "opacity-30"
                          }`}
                      >
                        A
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <ArrowDownwardIcon
                        fontSize="small"
                        className={
                          sortConfig.key === "status" &&
                            sortConfig.direction === "desc"
                            ? ""
                            : "opacity-30"
                        }
                      />
                      <span
                        className={`text-xs ${sortConfig.key === "status" &&
                          sortConfig.direction === "desc"
                          ? ""
                          : "opacity-30"
                          }`}
                      >
                        Z
                      </span>
                    </div>
                  </div>
                </div>
              </th>

              <th className="border p-2">PDF</th>
              <th className="border p-2">Acțiuni</th>
            </tr>
          </thead>
          <tbody>
            {currentOffers.length > 0 ? (
              currentOffers.map((offer, index) => (
                <tr
                  key={offer.id}
                  className={`border transition text-center ${darkMode
                    ? index % 2 === 0
                      ? "bg-gray-800 hover:bg-gray-700"
                      : "bg-gray-900 hover:bg-gray-700"
                    : index % 2 === 0
                      ? "bg-gray-100 hover:bg-gray-200"
                      : "bg-white hover:bg-gray-200"
                    }`}
                >
                  <td className="border p-2">{offer.nr}</td>
                  <td className="border p-2">{offer.userName}</td>
                  <td
                    className="border p-2 max-w-[200px] truncate"
                    title={offer.customerName}
                  >
                    {offer.customerName}
                  </td>
                  <td className="border p-2">{offer.telefon || "-"}</td>
                  <td className="border p-2">
                    {offer.subtotal.toFixed(2)} EUR
                  </td>
                  <td className="border p-2">{offer.tva.toFixed(2)} EUR</td>
                  <td className="border p-2">{offer.total.toFixed(2)} EUR</td>
                  <td className="border p-2">
                    {new Date(offer.createdAt).toISOString().split("T")[0]}
                  </td>
                  <td className="border p-2">
                    <Button
                      variant="outlined"
                      sx={{
                        minWidth: "110px", // Ez a beállítás biztosítja, hogy a gombok mindig ugyanakkora szélességűek legyenek
                        textAlign: "center",
                        justifyContent: "center",
                        borderColor:
                          offer.status === 1
                            ? "#66bb6a"
                            : offer.status === 2
                              ? "#f44336"
                              : "#2196f3",
                        color:
                          offer.status === 1
                            ? "#66bb6a"
                            : offer.status === 2
                              ? "#f44336"
                              : "#2196f3",
                        fontWeight: "bold",
                        "&:hover": {
                          backgroundColor:
                            offer.status === 1
                              ? "rgba(102, 187, 106, 0.1)"
                              : offer.status === 2
                                ? "rgba(244, 67, 54, 0.1)"
                                : "rgba(33, 150, 243, 0.1)",
                        },
                      }}
                      onClick={() => {
                        setOfferToToggle(offer);
                        setStatusToEdit(offer.status);
                        setRefusalReasonToView(offer.refusalReason);
                        setIsConfirmStatusModalOpen(true);
                      }}
                    >
                      {getStatusLabel(offer.status)}
                    </Button>
                  </td>

                  <td className="border p-2">
                    <GenerateOfferPDF offerData={offer} userData={userData} />
                  </td>
                  <td className="border p-2 flex justify-center gap-2">
                    <Button
                      variant="outlined"
                      startIcon={<EditIcon />}
                      sx={{
                        borderColor: darkMode ? "#90caf9" : "#0056B3",
                        color: darkMode ? "#90caf9" : "#0056B3",
                        "&:hover": {
                          borderColor: darkMode ? "#90caf9" : "#003B80",
                          backgroundColor: darkMode
                            ? "rgba(144, 202, 249, 0.1)"
                            : "rgba(0, 86, 179, 0.15)",
                        },
                      }}
                      onClick={() => {
                        setSelectedOffer(offer);
                        setIsEditModalOpen(true);
                      }}
                    >
                      Editare
                    </Button>

                    <Button
                      variant="outlined"
                      startIcon={<DeleteIcon />}
                      color="error"
                      onClick={() => {
                        setOfferToDelete(offer); // 🔥 Beállítjuk a törlendő ajánlatot
                        setIsDeleteModalOpen(true); // 🔥 Megnyitjuk a modált
                      }}
                    >
                      Ștergere
                    </Button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="text-center p-4 text-gray-400">
                  Nu există oferte disponibile.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* 🔄 Lapozás */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />

      {/* 🔥 Ajánlat szerkesztése modális ablak */}
      {isEditModalOpen && (
        <EditOfferModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onOfferEdited={handleOfferEdited}
          offerData={selectedOffer}
        />
      )}

      {/* 🔥 Ajánlat törlésére szolgáló modál */}
      <DeleteOfferModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setOfferToDelete(null); // 🔥 Nullázza az offerToDelete state-et
        }}
        onConfirm={async (id) => {
          if (!id) return;
          await deleteOffer(id);

          await Promise.all([fetchAllOffers(selectedYear), fetchUsers()]);

          setIsDeleteModalOpen(false);
        }}
        offer={offerToDelete}
      />
      <ConfirmOfferStatusModal
        open={isConfirmStatusModalOpen}
        onClose={() => {
          setIsConfirmStatusModalOpen(false);
          setTimeout(() => {
            setOfferToToggle(null);
            setStatusToEdit(null);
          }, 300);
        }}
        currentStatus={statusToEdit}
        initialRefusalReason={refusalReasonToView} // ⬅️ EZ HIÁNYZIK MOST
        onConfirm={async (newStatus, reason) => {
          const id = offerToToggle?.id;
          if (id != null) {
            await toggleOfferStatus(id, newStatus, reason); // ⬅️ reason is átadás
            await fetchAllOffers(selectedYear);
          }

          setIsConfirmStatusModalOpen(false);
          setTimeout(() => {
            setOfferToToggle(null);
            setStatusToEdit(null);
          }, 300);
        }}
      />
    </div>
  );
};

export default AllOffersList;
